import { useId, useState } from "react";
import { useForm } from "react-hook-form";
import { Navigate, useLocation, useNavigate } from "react-router-dom";

import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowRight, Loader2, Mail, ShieldCheck } from "lucide-react";

import { AuthLayout } from "@/app/layouts/AuthLayout";
import {
  type CredentialsInput,
  CredentialsSchema,
} from "@/features/auth/domain/credentials.schema";
import { AuthErrorAlert } from "@/features/auth/presentation/components/AuthErrorAlert";
import { AuthHeading } from "@/features/auth/presentation/components/AuthHeading";
import { PasswordField } from "@/features/auth/presentation/components/PasswordField";
import { Button } from "@/shared/components/ui/button";
import { Checkbox } from "@/shared/components/ui/checkbox";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/shared/components/ui/form";
import { Input } from "@/shared/components/ui/input";
import { cn } from "@/shared/lib/utils";
import { useAppDispatch, useAppSelector } from "@/store";
import { useSignInMutation } from "@/store/apis/adminApi.enhanced";
import { selectIsAal1, selectIsAuthenticated } from "@/store/selectors";
import { setRememberMe, setSession } from "@/store/slices/authSlice";

/**
 * Login — Paso 1 del flow de auth (email + password).
 *
 * Post-submit:
 *   - MFA verified → dashboard (o `from` si venía redirigida).
 *   - `enroll_required` / `challenge_required` → /mfa.
 *   - Error → `AuthErrorAlert` tipo-aware (rate limit vs credenciales).
 */
export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const isAal1 = useAppSelector(selectIsAal1);

  const from = (location.state as { from?: string } | null)?.from ?? "/";

  // Default `true` — comportamiento previo del backoffice y expectativa
  // en herramientas internas usadas desde la misma máquina. La usuaria
  // puede desmarcar si está en una compartida.
  const [remember, setRemember] = useState(true);
  const rememberId = useId();

  const form = useForm<CredentialsInput>({
    resolver: zodResolver(CredentialsSchema),
    defaultValues: { email: "", password: "" },
    mode: "onBlur",
  });

  const [signIn, signInState] = useSignInMutation();

  if (isAuthenticated) return <Navigate to={from} replace />;
  if (isAal1) return <Navigate to="/mfa" replace />;

  const onSubmit = form.handleSubmit(async (values) => {
    try {
      // Fijar el modo de persistencia ANTES del setSession — el reducer
      // lee `state.rememberMe` para decidir local vs sessionStorage.
      dispatch(setRememberMe(remember));
      const session = await signIn({ body: values }).unwrap();
      dispatch(setSession(session));
      if (session.mfa.status === "verified") {
        navigate(from, { replace: true });
      } else {
        navigate("/mfa", { replace: true });
      }
    } catch {
      // El error vive en `signInState.error`; el alert lo lee directo.
      // No seteamos form.setError root — el alert tipo-aware es mejor UX.
    }
  });

  return (
    <AuthLayout>
      <AuthHeading
        kicker="Bienvenida"
        title="Iniciar sesión"
        subtitle={
          <>
            Ingresa con tu cuenta administrativa. Toda cuenta requiere{" "}
            <span className="text-foreground">verificación en dos pasos</span>.
          </>
        }
      />

      <Form {...form}>
        <form onSubmit={onSubmit} className="space-y-5" noValidate>
          <FormField
            control={form.control}
            name="email"
            render={({ field, fieldState }) => (
              <FormItem>
                <FormLabel className="font-mono text-[10px] font-medium uppercase tracking-[0.22em] text-muted-foreground">
                  Correo electrónico
                </FormLabel>
                <FormControl>
                  <div className="relative">
                    <Mail
                      aria-hidden
                      className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                    />
                    <Input
                      type="email"
                      autoComplete="email"
                      spellCheck={false}
                      inputMode="email"
                      placeholder="tu@arya.pe"
                      aria-invalid={fieldState.invalid || undefined}
                      className={cn(
                        "h-12 pl-10 text-[15px] transition-shadow",
                        "focus-visible:shadow-ring-focus",
                      )}
                      {...field}
                    />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="password"
            render={({ field, fieldState }) => (
              <FormItem>
                <div className="flex items-baseline justify-between">
                  <FormLabel className="font-mono text-[10px] font-medium uppercase tracking-[0.22em] text-muted-foreground">
                    Contraseña
                  </FormLabel>
                  <a
                    href="mailto:soporte@arya.pe?subject=Reset%20de%20contrase%C3%B1a%20backoffice"
                    className="text-[12px] text-primary underline-offset-4 transition-colors hover:underline"
                  >
                    ¿Olvidaste tu contraseña?
                  </a>
                </div>
                <FormControl>
                  <PasswordField
                    placeholder="••••••••••"
                    aria-invalid={fieldState.invalid || undefined}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Remember me — editorial, con copy educativo */}
          <label
            htmlFor={rememberId}
            className={cn(
              "group -mx-3 flex cursor-pointer items-start gap-3 rounded-xl border border-transparent px-3 py-2.5 transition-colors",
              "hover:bg-muted/40",
              "focus-within:border-ring/40 focus-within:bg-muted/30",
            )}
          >
            <Checkbox
              id={rememberId}
              checked={remember}
              onCheckedChange={(checked) => setRemember(checked === true)}
              className="mt-0.5 h-[18px] w-[18px] data-[state=checked]:border-primary data-[state=checked]:bg-primary"
              aria-describedby={`${rememberId}-hint`}
            />
            <div className="min-w-0 flex-1 select-none">
              <div className="flex items-center gap-1.5">
                <span className="text-[13px] font-medium text-foreground">
                  Mantener sesión activa
                </span>
                <ShieldCheck
                  aria-hidden
                  className={cn(
                    "h-3 w-3 shrink-0 transition-opacity",
                    remember ? "text-primary opacity-100" : "opacity-0",
                  )}
                />
              </div>
              <p
                id={`${rememberId}-hint`}
                className="mt-0.5 text-[11.5px] leading-relaxed text-muted-foreground"
              >
                {remember
                  ? "Esta sesión se mantendrá al cerrar el navegador en este equipo."
                  : "Esta sesión se cerrará al cerrar el navegador."}
              </p>
            </div>
          </label>

          <AuthErrorAlert
            error={signInState.error}
            fallback="No pudimos iniciar tu sesión. Intenta de nuevo."
          />

          <Button
            type="submit"
            disabled={signInState.isLoading}
            className={cn(
              "h-12 w-full text-[14px] font-medium",
              "shadow-sm transition-all hover:shadow-md",
            )}
          >
            {signInState.isLoading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Ingresando…
              </>
            ) : (
              <>
                Continuar
                <ArrowRight className="h-4 w-4" />
              </>
            )}
          </Button>
        </form>
      </Form>

      <div className="mt-10 border-t border-border/60 pt-6">
        <p className="text-center text-[12px] text-muted-foreground">
          ¿Perdiste tu segundo factor?{" "}
          <a
            href="mailto:soporte@arya.pe?subject=Reset%20de%20MFA%20backoffice"
            className="font-medium text-foreground/80 underline-offset-4 transition-colors hover:text-foreground hover:underline"
          >
            Pide un reset a un super admin
          </a>
          .
        </p>
      </div>
    </AuthLayout>
  );
}
